import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Coins, ArrowRight, CheckCircle2, Wallet, Mail, ShieldCheck,
  AlertCircle, Sparkles, ExternalLink, Copy, Lock,
} from 'lucide-react';
import './ShareholderRewardsPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MOONSHOT_URL = 'https://moonshot.com/5DpvXUuPE2DXnH1GSHo2XOnV?ref=YwUnbl';

const ShareholderRewardsPage = () => {
  const [searchParams] = useSearchParams();
  const initialCode = (searchParams.get('code') || '').toUpperCase();

  const [view, setView] = useState(initialCode ? 'redeem' : 'intro'); // intro | claim | submitted | redeem | unlocked
  const [form, setForm] = useState({
    firstName: '',
    email: '',
    walletAddress: '',
    transactionUrl: '',
    note: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submittedMsg, setSubmittedMsg] = useState('');
  const [redeemCode, setRedeemCode] = useState(initialCode);
  const [redeemResult, setRedeemResult] = useState(null);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Auto-redeem if a code is in the URL
  useEffect(() => {
    if (initialCode) {
      tryRedeem(initialCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitClaim = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.firstName.trim() || !form.email.trim() || !form.walletAddress.trim()) {
      setError('Please fill in your first name, email, and Solana wallet address.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/coin/claim`, form);
      setSubmittedMsg(data?.message || 'Claim received. Watch your email.');
      setView('submitted');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not submit your claim. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const tryRedeem = async (code) => {
    setError('');
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/coin/redeem`, { code });
      setRedeemResult(data);
      setView('unlocked');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not redeem that code.');
      setView('redeem');
    } finally {
      setSubmitting(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard?.writeText(redeemCode);
  };

  return (
    <div className="srw-page" data-testid="shareholder-rewards-page">
      {/* ────── HERO ────── */}
      <section className="srw-hero">
        <div className="srw-hero-inner">
          <div className="srw-eyebrow"><Sparkles size={12} /> SHAREHOLDER REWARDS · LIMITED</div>
          <h1 className="srw-display">
            Invest <span className="srw-shimmer">$10</span> in $OHBCU.<br />
            Unlock <span className="srw-shimmer">50% Off</span> Our Professional Services.
          </h1>
          <p className="srw-subtitle">
            For a limited time, every visitor who buys at least $10 in The Collins Academy ($OHBCU) tokenized shares
            on Moonshot unlocks a one-time <strong>50% discount</strong> on our Credit Repair + Student Loan Removal service.
          </p>

          {/* ────── 3-STEP FLOW INDICATOR ────── */}
          <div className="srw-steps">
            <div className={`srw-step ${view === 'intro' ? 'srw-step-on' : 'srw-step-done'}`}>
              <span className="srw-step-num">1</span> Buy $10 on Moonshot
            </div>
            <span className="srw-step-divider" />
            <div className={`srw-step ${view === 'claim' || view === 'intro' ? '' : 'srw-step-done'} ${view === 'claim' ? 'srw-step-on' : ''}`}>
              <span className="srw-step-num">2</span> Submit Your Wallet
            </div>
            <span className="srw-step-divider" />
            <div className={`srw-step ${view === 'unlocked' ? 'srw-step-on' : ''}`}>
              <span className="srw-step-num">3</span> Redeem Discount
            </div>
          </div>
        </div>
      </section>

      {/* ────── INTRO ────── */}
      {view === 'intro' && (
        <section className="srw-section">
          <div className="srw-card srw-card-cta">
            <div className="srw-card-icon"><Coins size={32} /></div>
            <h2>Step 1: Buy $10 in $OHBCU</h2>
            <p>Click below to open Moonshot and purchase at least $10 worth of $OHBCU shares using your Solana wallet. The whole process takes about 60 seconds.</p>
            <a
              href={MOONSHOT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="srw-btn srw-btn-primary"
              data-testid="srw-buy-link"
              onClick={() => setTimeout(() => setView('claim'), 800)}
            >
              Open Moonshot — Buy $OHBCU <ExternalLink size={18} />
            </a>
            <p className="srw-card-hint">Already bought? <button type="button" className="srw-text-btn" onClick={() => setView('claim')} data-testid="srw-already-bought">Skip to Step 2 →</button></p>
          </div>

          <div className="srw-trust-row">
            <div className="srw-trust-item"><ShieldCheck size={16} /> Verified by Collins Academy admin</div>
            <div className="srw-trust-item"><Lock size={16} /> One-time, single-use code</div>
            <div className="srw-trust-item"><CheckCircle2 size={16} /> Refundable share purchase</div>
          </div>
        </section>
      )}

      {/* ────── CLAIM FORM ────── */}
      {view === 'claim' && (
        <section className="srw-section">
          <div className="srw-card srw-card-form">
            <div className="srw-card-icon"><Wallet size={32} /></div>
            <h2>Step 2: Submit Your Wallet</h2>
            <p>Paste the Solana wallet address you used to buy $OHBCU. We'll verify the purchase and email your one-time discount code within 24 hours.</p>

            <form onSubmit={submitClaim} className="srw-form" data-testid="srw-claim-form">
              <div className="srw-field">
                <label htmlFor="srw-fn">First Name</label>
                <input
                  id="srw-fn"
                  type="text"
                  value={form.firstName}
                  onChange={update('firstName')}
                  placeholder="Marcus"
                  data-testid="srw-input-firstName"
                  required
                />
              </div>
              <div className="srw-field">
                <label htmlFor="srw-em">Email Address</label>
                <input
                  id="srw-em"
                  type="email"
                  value={form.email}
                  onChange={update('email')}
                  placeholder="you@yourdomain.com"
                  data-testid="srw-input-email"
                  required
                />
              </div>
              <div className="srw-field">
                <label htmlFor="srw-wa">Solana Wallet Address</label>
                <input
                  id="srw-wa"
                  type="text"
                  value={form.walletAddress}
                  onChange={update('walletAddress')}
                  placeholder="e.g. 7xKXt...Z9mF (32–44 characters)"
                  data-testid="srw-input-wallet"
                  required
                />
                <span className="srw-field-hint">This is the wallet you used to buy $OHBCU on Moonshot. Find it in your wallet app under "Receive".</span>
              </div>
              <div className="srw-field">
                <label htmlFor="srw-tx">Solscan Transaction URL <span className="srw-optional">(optional, speeds up approval)</span></label>
                <input
                  id="srw-tx"
                  type="url"
                  value={form.transactionUrl}
                  onChange={update('transactionUrl')}
                  placeholder="https://solscan.io/tx/..."
                  data-testid="srw-input-txurl"
                />
              </div>

              {error && (<div className="srw-error" data-testid="srw-error"><AlertCircle size={14} /> {error}</div>)}

              <button
                type="submit"
                className="srw-btn srw-btn-primary srw-btn-block"
                disabled={submitting}
                data-testid="srw-submit-claim"
              >
                {submitting ? 'Submitting…' : (<>Submit My Claim <ArrowRight size={18} /></>)}
              </button>
              <p className="srw-card-hint" style={{ textAlign: 'center', marginTop: 18 }}>
                <ShieldCheck size={12} /> Your information is encrypted &amp; never shared
              </p>
            </form>
          </div>
        </section>
      )}

      {/* ────── SUBMITTED (waiting for admin approval) ────── */}
      {view === 'submitted' && (
        <section className="srw-section">
          <div className="srw-card srw-card-success">
            <div className="srw-card-icon srw-card-icon-success"><CheckCircle2 size={42} /></div>
            <h2>Claim Received</h2>
            <p>{submittedMsg}</p>
            <div className="srw-card-meta">
              <div><Mail size={14} /> Watch your inbox — your discount code will arrive within 24 hours.</div>
              <div><ShieldCheck size={14} /> Once approved, your code is single-use and tied to your wallet.</div>
            </div>
          </div>
        </section>
      )}

      {/* ────── REDEEM (user has code, hasn't redeemed yet) ────── */}
      {view === 'redeem' && (
        <section className="srw-section">
          <div className="srw-card srw-card-form">
            <div className="srw-card-icon"><Sparkles size={32} /></div>
            <h2>Step 3: Redeem Your Code</h2>
            <p>Enter the discount code we emailed you to unlock your 50% off Credit Repair + Student Loan service.</p>

            <form
              className="srw-form"
              onSubmit={(e) => { e.preventDefault(); tryRedeem(redeemCode.trim().toUpperCase()); }}
              data-testid="srw-redeem-form"
            >
              <div className="srw-field">
                <label htmlFor="srw-code">Discount Code</label>
                <input
                  id="srw-code"
                  type="text"
                  value={redeemCode}
                  onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
                  placeholder="OHBCU-SHRH-XXXXXX"
                  data-testid="srw-input-code"
                  style={{ fontFamily: 'Courier New, monospace', letterSpacing: 2 }}
                  required
                />
              </div>
              {error && (<div className="srw-error"><AlertCircle size={14} /> {error}</div>)}
              <button
                type="submit"
                className="srw-btn srw-btn-primary srw-btn-block"
                disabled={submitting}
                data-testid="srw-redeem-submit"
              >
                {submitting ? 'Verifying…' : (<>Unlock My Discount <ArrowRight size={18} /></>)}
              </button>
            </form>
          </div>
        </section>
      )}

      {/* ────── UNLOCKED — discount link revealed ────── */}
      {view === 'unlocked' && redeemResult && (
        <section className="srw-section">
          <div className="srw-card srw-card-unlocked">
            <div className="srw-card-icon srw-card-icon-success"><Sparkles size={42} /></div>
            <div className="srw-unlocked-eyebrow">SHAREHOLDER DISCOUNT ACTIVE</div>
            <h2>You're unlocked, {redeemResult.firstName || 'Shareholder'}.</h2>
            <p>Your one-time 50% discount on the Credit Repair + Student Loan Removal service is ready.</p>

            <div className="srw-discount-banner">
              <div className="srw-discount-eyebrow">SHAREHOLDER PRICE LOCKED IN</div>
              <div className="srw-discount-headline">50% Off Professional Services</div>
              <div className="srw-discount-sub">One-time use · tied to your wallet</div>
            </div>

            <a
              href={redeemResult.discountUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="srw-btn srw-btn-primary srw-btn-block"
              data-testid="srw-checkout-link"
            >
              Complete My Discounted Checkout <ArrowRight size={18} />
            </a>

            <div className="srw-unlocked-code">
              <span>Reference code:</span>
              <code>{redeemCode}</code>
              <button type="button" className="srw-text-btn" onClick={copyCode} data-testid="srw-copy-code"><Copy size={12} /> Copy</button>
            </div>
            <p className="srw-card-hint">This code is single-use and now locked to your purchase. Bookmark this page if you need to reference it.</p>
          </div>
        </section>
      )}

      {/* ────── ALWAYS-ON FOOTER: Already have a code? ────── */}
      {view !== 'unlocked' && view !== 'redeem' && (
        <section className="srw-section srw-section-tight">
          <div className="srw-have-code">
            <span>Already have a discount code?</span>
            <button type="button" className="srw-btn srw-btn-ghost" onClick={() => setView('redeem')} data-testid="srw-go-redeem">
              Redeem It Now <ArrowRight size={16} />
            </button>
          </div>
        </section>
      )}

      <p className="srw-disclosure">
        Shareholder rewards are issued at the sole discretion of The Collins Academy. Discount applies to a single purchase of the Credit Repair + Student Loan Removal service. Tokenized share purchases are independent of any service offering — consult your own advisors.
      </p>
    </div>
  );
};

export default ShareholderRewardsPage;
